import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, Upload, X, FileVideo, Film } from 'lucide-react'
import { useToast } from '../../contexts/ToastContext'
import Drawer from '../ui/Drawer'
import broadcastAdminService, {
  RefLeague, RefTeam, RefVenue, RefCompetitionStage
} from '../../services/broadcastAdminService'
import { composeTitle, seasonOptions, likelySeason } from '../../utils/broadcastNaming'

interface Props {
  isOpen: boolean
  customerId: string
  organizationName?: string
  onDone: () => Promise<void> | void
  onCancel: () => void
}

const input =
  'w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-colors'
const label = 'block text-xs font-medium text-gray-700 mb-1'

/**
 * Support-driven upload. Two steps, in this order deliberately:
 *   1. bytes go STRAIGHT to blob storage with a short-lived SAS
 *   2. only once they land do we write the broadcast row
 *
 * An abandoned upload therefore leaves no orphan record, and the API never
 * handles a 4-5 GB body.
 */
const BroadcastUploadForm: React.FC<Props> = ({
  isOpen, customerId, organizationName, onDone, onCancel
}) => {
  const { showSuccess, showError, showInfo } = useToast()
  const [file, setFile] = useState<File | null>(null)
  const [title, setTitle] = useState('')
  const [eventDate, setEventDate] = useState('')
  const [season, setSeason] = useState('')
  const [stageId, setStageId] = useState('')
  // once support edits the title by hand we stop composing over the top of them
  const [titleTouched, setTitleTouched] = useState(false)
  const [leagueId, setLeagueId] = useState('')
  const [homeTeamId, setHomeTeamId] = useState('')
  const [awayTeamId, setAwayTeamId] = useState('')
  const [venueId, setVenueId] = useState('')

  const [leagues, setLeagues] = useState<RefLeague[]>([])
  const [teams, setTeams] = useState<RefTeam[]>([])
  const [venues, setVenues] = useState<RefVenue[]>([])
  const [showAllVenues, setShowAllVenues] = useState(false)
  const [stages, setStages] = useState<RefCompetitionStage[]>([])

  const [progress, setProgress] = useState(0)
  const [busy, setBusy] = useState(false)
  const abort = useRef<AbortController | null>(null)
  const fileInput = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        // sport is resolved from the chosen league, so the sports list itself
        // is not needed here
        const [l, t, v, st] = await Promise.all([
          broadcastAdminService.listReference<RefLeague>('leagues'),
          broadcastAdminService.listReference<RefTeam>('teams'),
          broadcastAdminService.listReference<RefVenue>('venues'),
          broadcastAdminService.listReference<RefCompetitionStage>('competition-stages')
        ])
        setLeagues(l.data || []); setTeams(t.data || [])
        setVenues(v.data || []); setStages(st.data || [])
      } catch {
        showError('Could not load reference data', 'Sports, leagues and teams are unavailable')
      }
    })()
  }, [showError])

  const leagueGroups = useMemo(() => {
    const by = new Map<string, RefLeague[]>()
    for (const l of leagues) {
      const k = l.sport_name || 'Other'
      const g = by.get(k); g ? g.push(l) : by.set(k, [l])
    }
    return [...by.entries()]
  }, [leagues])

  const league = leagues.find((l) => l.id === leagueId)
  const leagueTeams = leagueId ? teams.filter((t) => t.league_id === leagueId) : teams

  /**
   * Venues this league actually plays at.
   *
   * There is no league on a venue — the link is teams.home_venue_id — so the
   * league's grounds are its clubs' home grounds. Filtered client-side like
   * the team list, because the teams are already loaded and already carry it.
   *
   * NEUTRAL SITES ARE REAL: a showcase game, a final, a tournament, and the
   * Pioneer League's travel team which has no home ground at all. So this
   * narrows the list rather than restricting it, and `showAllVenues` lets
   * someone reach a ground no club in the league owns. The currently-selected
   * venue is always kept, or editing a neutral-site broadcast would silently
   * clear it.
   */
  const leagueVenueIds = new Set(
    leagueTeams.map((t) => t.home_venue_id).filter(Boolean) as string[]
  )
  const venuesInLeague = venues.filter((v) => leagueVenueIds.has(v.id))
  const shownVenues = (!leagueId || showAllVenues || venuesInLeague.length === 0)
    ? venues
    : venues.filter((v) => leagueVenueIds.has(v.id) || v.id === venueId)
  const seasons = seasonOptions(league?.season_format)

  // Compose the title from the structured fields. Teams, stage and date are
  // already columns — retyping them into a string only guarantees three
  // spellings of the same fixture.
  useEffect(() => {
    if (titleTouched) return
    // No filename fallback here, deliberately. The file is usually chosen
    // first, so falling back would put the broadcaster's name
    // ("Round 10 Game 1 - Brisbane @ Sydney ABL 2526") in the field before any
    // structured field is set — which looks like the final answer and hides
    // the fact that it composes. The filename is used only at submit, and only
    // if nothing better exists.
    setTitle(composeTitle({
      awayTeamName: teams.find((t) => t.id === awayTeamId)?.name,
      homeTeamName: teams.find((t) => t.id === homeTeamId)?.name,
      stageName: stages.find((x) => x.id === stageId)?.name,
      eventDate
    }))
  }, [awayTeamId, homeTeamId, stageId, eventDate, teams, stages, titleTouched])

  // season follows the league's shape and the event date
  useEffect(() => {
    if (!league) return
    setSeason((cur) => (cur && seasons.includes(cur) ? cur : likelySeason(league.season_format, eventDate)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId, eventDate, league?.season_format])

  const pick = (f: File | null) => {
    setFile(f)
  }

  /**
   * Clearing has to reset the input's own value, not just React state —
   * otherwise re-choosing the SAME file fires no change event and the picker
   * appears to do nothing.
   */
  const clearFile = () => {
    setFile(null)
    if (fileInput.current) fileInput.current.value = ''
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file) { showError('No file', 'Choose a broadcast file to upload'); return }
    if (homeTeamId && homeTeamId === awayTeamId) {
      showError('Teams must differ', 'Home and away cannot be the same team'); return
    }

    setBusy(true); setProgress(0)
    abort.current = new AbortController()
    try {
      const ticket = await broadcastAdminService.getUploadUrl(customerId, {
        fileName: file.name, fileSizeBytes: file.size
      })

      await broadcastAdminService.uploadToBlob(
        ticket.data.uploadUrl, file, setProgress, abort.current.signal
      )

      await broadcastAdminService.createBroadcast(customerId, {
        // last resort only: an untitled broadcast is worse than a messy one
        title: title.trim() || file.name.replace(/\.[^.]+$/, ''),
        eventDate: eventDate || null,
        season: season || null,
        competitionStageId: stageId || null,
        leagueId: leagueId || null,
        sportId: league?.sport_id || null,
        homeTeamId: homeTeamId || null,
        awayTeamId: awayTeamId || null,
        venueId: venueId || null,
        blobPath: ticket.data.blobPath,
        fileSizeBytes: file.size
      })

      showSuccess('Broadcast uploaded', `${title || file.name} is in the library`)
      await onDone()
    } catch (err) {
      // Detect the abort by signal/name, NOT by message text: the browser's
      // AbortError reads "signal is aborted without reason", which matches no
      // sensible pattern and surfaced as "Upload failed".
      const aborted =
        abort.current?.signal.aborted ||
        (err instanceof DOMException && err.name === 'AbortError') ||
        (err instanceof Error && err.name === 'AbortError')

      if (aborted) {
        // Staged blocks are never committed, and Azure discards uncommitted
        // blocks after 7 days — so nothing is stored and nothing is charged.
        showInfo('Upload cancelled', 'Nothing was stored.')
      } else {
        showError('Upload failed', err instanceof Error ? err.message : 'Please try again')
      }
    } finally {
      setBusy(false); abort.current = null
    }
  }

  // Closing mid-transfer would orphan staged blocks and lose the progress the
  // user is watching, so the drawer refuses to close while busy.
  const requestClose = () => { if (!busy) onCancel() }

  return (
    <Drawer
      isOpen={isOpen}
      onClose={requestClose}
      title="Add broadcast"
      description={organizationName ? `Upload a game file for ${organizationName}` : 'Upload a game file'}
      icon={<Film className="h-5 w-5" />}
      width="lg"
    >
    <form onSubmit={submit} className="space-y-3">

      <div>
        <label className={label}>File <span className="text-red-500">*</span></label>
        {/* The file stays changeable until Upload is pressed. Once busy the
            input is disabled, because swapping the file mid-transfer would
            leave half of one file and half of another staged in storage. */}
        {file ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-800 max-w-full">
              <FileVideo className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
              <span className="truncate max-w-[22rem]">{file.name}</span>
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {file.size >= 1e9 ? `${(file.size / 1e9).toFixed(2)} GB` : `${Math.round(file.size / 1e6)} MB`}
              </span>
            </span>
            {!busy && (
              <>
                <button type="button" onClick={() => fileInput.current?.click()}
                        className="text-xs font-medium text-blue-600 hover:text-blue-700">
                  Change
                </button>
                <button type="button" onClick={clearFile}
                        className="inline-flex items-center text-xs font-medium text-gray-500 hover:text-gray-700">
                  <X className="h-3 w-3 mr-0.5" />Remove
                </button>
              </>
            )}
          </div>
        ) : (
          <p className="text-xs text-gray-500">No file chosen</p>
        )}
        <input ref={fileInput} type="file" accept="video/*,.mp4,.mkv,.mov,.ts"
               onChange={(e) => pick(e.target.files?.[0] ?? null)}
               disabled={busy}
               className={file ? 'hidden' : 'mt-1 text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-white file:border file:border-gray-300 file:text-gray-700 hover:file:bg-gray-50'} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={label}>Title</label>
          <input value={title}
                 onChange={(e) => {
                   setTitle(e.target.value)
                   // clearing it hands control back: an empty field should fill
                   // itself again rather than stay stubbornly blank
                   setTitleTouched(e.target.value.trim().length > 0)
                 }}
                 placeholder="Composed from teams, stage and date"
                 className={input} disabled={busy} />
          <p className="text-xs text-gray-500 mt-1">
            {titleTouched
              ? 'Edited by hand — it will no longer update automatically.'
              : title
                ? 'Composed from teams, stage and date. Type to override.'
                : 'Fills in as you choose teams and a date. Type to name it yourself.'}
          </p>
        </div>
        <div>
          <label className={label}>Event date</label>
          <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} className={input} disabled={busy} />
        </div>
      </div>

      <div>
        <label className={label}>League</label>
        <select value={leagueId} onChange={(e) => { setLeagueId(e.target.value); setHomeTeamId(''); setAwayTeamId('') }}
                className={input} disabled={busy}>
          <option value="">—</option>
          {/* Grouped by sport, which the API already orders by. Without the
              optgroup the picker shows only short names (MLB, ABL, NBA...)
              and the grouping is invisible — the list reads as unsorted. */}
          {leagueGroups.map(([sport, ls]) => (
            <optgroup key={sport} label={sport}>
              {ls.map((l) => <option key={l.id} value={l.id}>{l.short_name || l.name}</option>)}
            </optgroup>
          ))}
        </select>
      </div>

      {/* away first, matching the "{Away} @ {Home}" title the form composes */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={label}>Away team</label>
          <select value={awayTeamId} onChange={(e) => setAwayTeamId(e.target.value)} className={input} disabled={busy}>
            <option value="">—</option>
            {leagueTeams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>Home team</label>
          <select value={homeTeamId} onChange={(e) => setHomeTeamId(e.target.value)} className={input} disabled={busy}>
            <option value="">—</option>
            {leagueTeams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="flex items-baseline justify-between gap-2">
            <label className={label}>Venue</label>
            {leagueId && venuesInLeague.length > 0 && venuesInLeague.length < venues.length && (
              <button
                type="button"
                onClick={() => setShowAllVenues((v) => !v)}
                className="text-[11px] text-blue-600 hover:text-blue-700"
              >
                {showAllVenues
                  ? `Just this league (${venuesInLeague.length})`
                  : `All venues (${venues.length})`}
              </button>
            )}
          </div>
          <select value={venueId} onChange={(e) => setVenueId(e.target.value)} className={input} disabled={busy}>
            <option value="">—</option>
            {shownVenues.map((v) => <option key={v.id} value={v.id}>{v.name}{v.city ? ` — ${v.city}` : ''}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>Season</label>
          {/* Not disabled when no league is chosen. A greyed-out field explained
              only by a hover tooltip reads as broken — it just offers calendar
              years until a league narrows the shape. */}
          <select value={season} onChange={(e) => setSeason(e.target.value)} className={input}
                  disabled={busy}>
            <option value="">—</option>
            {seasons.map((sv) => <option key={sv} value={sv}>{sv}</option>)}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            {league
              ? league.season_format === 'split'
                ? `${league.short_name || league.name} crosses the new year`
                : `${league.short_name || league.name} runs inside one year`
              : 'Pick a league to match its season shape'}
          </p>
        </div>
        <div>
          <label className={label}>Stage</label>
          <select value={stageId} onChange={(e) => setStageId(e.target.value)} className={input} disabled={busy}>
            <option value="">—</option>
            {stages.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </div>
      </div>

      {busy && (
        <div>
          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
            <span>Uploading straight to storage…</span>
            <span className="font-medium">{Math.round(progress * 100)}%</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-blue-600 transition-all" style={{ width: `${progress * 100}%` }} />
          </div>
          <button type="button" onClick={() => abort.current?.abort(new DOMException('Cancelled by user', 'AbortError'))}
                  className="text-xs text-red-600 hover:text-red-700 mt-1.5">
            Cancel upload
          </button>
        </div>
      )}

      <p className="text-xs text-gray-500">
        Sport is taken from the league and the season follows its shape —
        {' '}{league?.season_format === 'split' ? '2025-26 for a season that crosses the new year' : '2026 for a season inside one year'}.
        Nothing is analysed or charged yet.
      </p>

      <div className="flex gap-2 pt-1">
        <button type="submit" disabled={busy || !file}
          className="flex-1 inline-flex items-center justify-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm">
          {busy ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Uploading…</>
                : <><Upload className="h-3.5 w-3.5 mr-1.5" />Upload</>}
        </button>
        <button type="button" onClick={requestClose} disabled={busy}
          className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
          Cancel
        </button>
      </div>
    </form>
    </Drawer>
  )
}

export default BroadcastUploadForm
